<template>
  <div class="required-types-page">
    <div class="required-types-page__header">
      <h2 class="required-types-page__title">Типы потребностей материалов</h2>
      <q-btn
        color="primary"
        label="Добавить тип"
        icon="add"
        no-caps
        @click="onCreateClick"
      />
    </div>

    <CustomTable
      :rowsData="rows"
      :columns="columns"
      :loading="isLoading"
      :show-search="true"
      :custom-body="true"
      :update-data="loadData"
    >
      <template #customBody="props">
        <q-tr :props="props">
          <custom-table-cell align="left">
            {{ props.row.required_type_id }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.required_type_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.required_type_code || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <q-icon
              :name="props.row.is_active ? 'check_circle' : 'cancel'"
              :color="props.row.is_active ? 'positive' : 'grey-5'"
              size="sm"
            />
          </custom-table-cell>
          <custom-table-cell align="center">
            <div class="row no-wrap items-center justify-center q-gutter-xs">
              <q-btn
                unelevated dense
                icon="edit"
                size="sm"
                color="primary"
                text-color="white"
                style="border-radius: 6px; width: 30px; height: 30px;"
                @click.stop="onEditClick(props.row)"
              />
              <q-btn
                unelevated dense
                :color="props.row.executor_cnt ? 'primary' : 'negative'"
                text-color="white"
                @click.stop="onRowClick(props.row)"
                style="border-radius: 6px; height: 30px; padding: 0 8px;"
              >
                <q-icon name="person" size="16px" />
                <span style="font-size: 14px; font-weight: 700; margin-left: 3px;">{{ props.row.executor_cnt || 0 }}</span>
                <q-tooltip>Снабженцы</q-tooltip>
              </q-btn>
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <!-- Диалог редактирования/добавления типа -->
    <q-dialog v-model="dialog" persistent>
      <q-card style="min-width: 480px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ form.required_type_id ? 'Редактировать' : 'Добавить' }} тип потребности</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <q-form @submit.prevent="onSave" class="q-gutter-md">
            <q-input
              v-model="form.required_type_name"
              label="Название типа *"
              outlined
              dense
              :rules="[val => !!val || 'Обязательное поле']"
            />

            <q-input
              v-model="form.required_type_code"
              label="Код"
              outlined
              dense
            />

            <q-checkbox
              v-model="form.is_active"
              label="Активность"
            />

            <div class="row justify-end q-gutter-sm q-mt-md">
              <q-btn flat label="Отмена" color="grey" v-close-popup no-caps />
              <q-btn
                unelevated
                label="Сохранить"
                color="primary"
                type="submit"
                :loading="isSaving"
                no-caps
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог снабженцов -->
    <q-dialog v-model="executorsDialog" persistent @hide="onExecutorsDialogClose">
      <q-card style="min-width: 520px; max-width: 600px;">
        <q-card-section class="row items-center q-pb-none">
          <div>
            <div class="text-h6">Назначенные снабженцы</div>
            <div class="text-caption text-grey">{{ selectedType && selectedType.required_type_name }}</div>
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <!-- Добавление снабженца -->
          <div class="row q-gutter-sm q-mb-md items-center">
            <q-select
              v-model="newExecutorId"
              :options="availableExecutors"
              option-value="employee_id"
              option-label="fio"
              emit-value
              map-options
              label="Выберите снабженца"
              outlined
              dense
              use-input
              input-debounce="0"
              @filter="filterExecutors"
              :loading="isLoadingAvailable"
              style="flex: 1;"
            >
              <template #no-option>
                <q-item>
                  <q-item-section class="text-grey text-center" style="font-size:13px">Не найдено</q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-btn
              color="primary"
              icon="add"
              label="Добавить"
              no-caps
              unelevated
              :loading="isAdding"
              :disable="!newExecutorId"
              @click="onAddExecutor"
            />
          </div>

          <!-- Список назначенных -->
          <div v-if="isLoadingExecutors" class="text-center q-py-md">
            <q-spinner color="primary" size="28px" />
          </div>
          <div v-else-if="!assignedExecutors.length" class="text-grey text-center q-py-md">
            Снабженцы не назначены
          </div>
          <q-list v-else bordered separator class="rounded-borders">
            <q-item v-for="exec in assignedExecutors" :key="exec.required_type_executor_id">
              <q-item-section>
                <q-item-label>{{ exec.executor_fio || exec.executor_name || '-' }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  flat dense round
                  icon="delete"
                  color="negative"
                  size="sm"
                  :loading="deletingId === exec.required_type_executor_id"
                  @click="onDeleteExecutor(exec)"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import instance from "@/services/api";
import CustomTable from "@/components/common/CustomTable/index.vue";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell.vue";

const columns = [
  { name: "required_type_id", label: "ID", align: "left", field: "required_type_id", sortable: true },
  { name: "required_type_name", label: "Тип потребности материала", align: "left", field: "required_type_name", sortable: true },
  { name: "required_type_code", label: "Код", align: "left", field: "required_type_code", sortable: true },
  { name: "is_active", label: "Активность", align: "center", field: "is_active" },
  { name: "actions", label: "", align: "center" },
];

export default {
  name: "RequiredTypes",
  components: { CustomTable, CustomTableCell },
  data() {
    return {
      rows: [],
      columns,
      isLoading: false,
      dialog: false,
      isSaving: false,
      form: {
        required_type_id: null,
        required_type_name: "",
        required_type_code: "",
        is_active: true,
      },

      executorsDialog: false,
      selectedType: null,
      assignedExecutors: [],
      isLoadingExecutors: false,

      availableExecutors: [],
      allAvailableExecutors: [],
      isLoadingAvailable: false,
      newExecutorId: null,
      isAdding: false,
      deletingId: null,
    };
  },
  mounted() {
    this.loadData();
    this.loadAllEmployees();
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        const { data } = await instance().get("/required/required-type-read/");
        this.rows = data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    onCreateClick() {
      this.form = {
        required_type_id: null,
        required_type_name: "",
        required_type_code: "",
        is_active: true,
      };
      this.dialog = true;
    },
    onEditClick(row) {
      this.form = {
        required_type_id: row.required_type_id,
        required_type_name: row.required_type_name || "",
        required_type_code: row.required_type_code || "",
        is_active: !!row.is_active,
      };
      this.dialog = true;
    },
    async onSave() {
      this.isSaving = true;
      try {
        await instance().post("/required/required-type-upd/", {
          required_type_id: this.form.required_type_id,
          required_type_name: this.form.required_type_name,
          required_type_code: this.form.required_type_code,
          is_active: this.form.is_active,
        });
        this.dialog = false;
        await this.loadData();
      } catch (e) {
        console.error(e);
      } finally {
        this.isSaving = false;
      }
    },

    async onRowClick(row) {
      this.selectedType = row;
      this.newExecutorId = null;
      this.availableExecutors = [...this.allAvailableExecutors];
      this.executorsDialog = true;
      await this.loadAssignedExecutors(row.required_type_id);
    },

    async loadAssignedExecutors(required_type_id) {
      this.isLoadingExecutors = true;
      try {
        const { data } = await instance().get(`/required/required-type-executor-read/${required_type_id}/`);
        this.assignedExecutors = data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingExecutors = false;
      }
    },

    async loadAllEmployees() {
      this.isLoadingAvailable = true;
      try {
        const { data } = await instance().get('/required/read-employee-for-required-type/');
        this.allAvailableExecutors = data?.data || [];
        this.availableExecutors = [...this.allAvailableExecutors];
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingAvailable = false;
      }
    },

    filterExecutors(val, update) {
      update(() => {
        if (!val) {
          this.availableExecutors = [...this.allAvailableExecutors];
        } else {
          const q = val.toLowerCase();
          this.availableExecutors = this.allAvailableExecutors.filter(
            e => (e.fio || "").toLowerCase().includes(q)
          );
        }
      });
    },

    async onAddExecutor() {
      if (!this.newExecutorId || !this.selectedType) return;
      this.isAdding = true;
      try {
        await instance().post("/required/required-type-executor-upd/", {
          required_type_id: this.selectedType.required_type_id,
          executor_id: this.newExecutorId,  // employee_id из read_employee_for_required_type
          can_edit: true,
        });
        this.newExecutorId = null;
        await this.loadAssignedExecutors(this.selectedType.required_type_id);
      } catch (e) {
        console.error(e);
      } finally {
        this.isAdding = false;
      }
    },

    onExecutorsDialogClose() {
      if (!this.selectedType) return;
      const row = this.rows.find(r => r.required_type_id === this.selectedType.required_type_id);
      if (row) row.executor_cnt = this.assignedExecutors.length;
    },

    async onDeleteExecutor(exec) {
      this.deletingId = exec.required_type_executor_id;
      try {
        await instance().post("/required/required-type-executor-del/", {
          required_type_executor_id: exec.required_type_executor_id,
        });
        await this.loadAssignedExecutors(this.selectedType.required_type_id);
      } catch (e) {
        console.error(e);
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style scoped>
.required-types-page {
  padding: 16px;
}
.required-types-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.required-types-page__title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
</style>
